import React from 'react';
import { Car } from 'lucide-react';
import LoanPageTemplate from '../../components/LoanPageTemplate';

const CarLoans = () => (
  <LoanPageTemplate
    icon={Car}
    path="/car-loans"
    title="Car Loans"
    subtitle="Drive away with a competitive car loan rate whether you are buying new, used, or upgrading. Pre-approval available from over 20 lenders."
    description="Australians finance over 50% of vehicle purchases, yet many accept dealer finance without comparing alternatives. Dealer finance margins can add thousands to your total cost over the loan term. We access wholesale car loan rates from specialist lenders, banks, and credit unions so you walk into the dealership with approved finance and full buying power."
    features={[
      {
        title: 'New & Used Vehicle Finance',
        description: 'We offer competitive rates on vehicles up to 15 years old at the end of the loan term. New car rates start from 5.49% and used car rates from 5.99% depending on the lender and your profile. Rates are indicative only, subject to lender criteria and individual assessment. Contact us for a personalised quote.'
      },
      {
        title: 'Novated Lease Arrangements',
        description: 'A novated lease lets you salary package your vehicle costs including fuel, insurance, registration, and maintenance. We calculate the after-tax savings compared to buying outright or via a standard car loan.'
      },
      {
        title: 'Balloon Payment Options',
        description: 'Reduce your monthly repayments by deferring a lump sum (balloon) to the end of the loan. We help you decide whether a balloon payment makes sense or simply delays the cost.'
      },
      {
        title: 'Broker vs Dealer Finance',
        description: 'A broker can help you compare finance options from multiple lenders, giving you a clearer picture of what is available before making a decision.'
      },
      {
        title: 'Pre-Approved Car Loans',
        description: 'Get approved before you shop so you can negotiate the vehicle price as a cash buyer. Pre-approval is valid for up to 90 days and gives you a clear budget to work with.'
      },
      {
        title: 'Electric & Hybrid Vehicle Finance',
        description: 'Some lenders offer discounted rates for electric and hybrid vehicles. Combined with the Fringe Benefits Tax exemption on novated leases for EVs, the savings can be substantial.'
      }
    ]}
    process={[
      {
        title: 'Budget & Vehicle Assessment',
        description: 'We discuss your budget, the type of vehicle you want, and whether a loan, lease, or novated lease is the best structure for your tax and cash flow situation.'
      },
      {
        title: 'Rate Comparison & Pre-Approval',
        description: 'We compare rates from over 20 vehicle finance lenders and secure pre-approval so you know your maximum spend and exact repayment amount before visiting a dealer.'
      },
      {
        title: 'Purchase & Documentation',
        description: 'Once you find your vehicle, we finalise the loan documents and coordinate funding. For dealer purchases, the lender pays the dealer directly at settlement.'
      },
      {
        title: 'Settlement & Registration',
        description: 'Funds are released to the seller or dealer, and the security interest is registered on the PPSR. We confirm your repayment schedule and direct debit details.'
      }
    ]}
    faqs={[
      {
        q: 'Should I get finance from the dealer or a broker?',
        a: 'Dealers often offer convenience at the point of sale, while brokers can provide access to multiple lenders and help you compare different finance options. Getting pre-approved before visiting a dealer can give you a clearer understanding of your options.'
      },
      {
        q: 'What is a balloon payment and should I use one?',
        a: 'A balloon payment is a lump sum due at the end of your car loan, typically 20-40% of the purchase price. It lowers your monthly repayments but means you owe a large amount at the end. You can pay it off, refinance it, or trade in the vehicle. Balloons work well for business use vehicles but can be risky for personal buyers if the car depreciates below the balloon amount.'
      },
      {
        q: 'Can I finance a used car or a private sale vehicle?',
        a: 'Yes. Most lenders finance used vehicles up to 12-15 years old at the end of the loan term. Private sale vehicles can also be financed, though some lenders restrict this to vehicles under a certain age or above a minimum value. We source lenders that accommodate your specific purchase scenario.'
      },
      {
        q: 'What is a novated lease and who is it suitable for?',
        a: 'A novated lease is a three-way agreement between you, your employer, and a finance company. Your employer makes lease payments from your pre-tax salary, reducing your taxable income. Running costs like fuel, insurance, and servicing can also be included. It is most beneficial for employees in higher tax brackets driving newer vehicles. Since July 2022, electric vehicles on novated leases are exempt from Fringe Benefits Tax.'
      },
      {
        q: 'How much deposit do I need for a car loan?',
        a: 'Most car loans do not require a deposit, and you can finance 100% of the vehicle purchase price. However, contributing a deposit reduces your loan amount, lowers your repayments, and may help you qualify for a better interest rate. A trade-in vehicle can also serve as your deposit. We assess your situation and advise on the optimal deposit amount.'
      }
    ]}
    loanType="Car Loan"
    color="from-[#00594C] to-[#003d35]"
  />
);

export default CarLoans;
